import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';

const AddUpdate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [loading, setLoading] = useState(false);
    const [specialistType, setSpecialistType] = useState('existing'); // 'existing' or 'new'
    
    const [phoneCode, setPhoneCode] = useState('+255');
    
    const [formData, setFormData] = useState({
        specialist_id: '',
        new_specialist: {
            name: '',
            email: '',
            phone: '',
            username: '',
            password: '',
        },
        visit_date: '',
        visit_time_from: '',
        visit_time_to: '',
        clinic_fee: '',
    });

    useEffect(() => {
        if (isEdit) {
            fetchVisit();
        }
    }, [id]);

    const fetchVisit = async () => {
        setLoading(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: `admin/specialist-visits/${id}`,
            });
            if (response.data.status) {
                const data = response.data.data.visit;
                // Normalize formats for date/time inputs (API may return ISO date & HH:MM:SS times)
                const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');
                const toTimeInput = (v) => {
                    if (!v) return '';
                    const m = String(v).match(/(\d{2}):(\d{2})/);
                    return m ? `${m[1]}:${m[2]}` : '';
                };
                setFormData({
                    specialist_id: data.specialist_id,
                    new_specialist: { name: '', email: '', phone: '', username: '', password: '' },
                    visit_date: toDateInput(data.visit_date),
                    visit_time_from: toTimeInput(data.visit_time_from),
                    visit_time_to: toTimeInput(data.visit_time_to),
                    clinic_fee: data.clinic_fee,
                });
                setSpecialistType('existing'); // Edit mode always assumes existing specialist
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching visit');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const payload = {
            ...formData,
            specialist_type: specialistType,
        };
        
        // If existing, we don't need new_specialist data
        if (specialistType === 'existing') {
            delete payload.new_specialist;
        } else {
            delete payload.specialist_id;
            // Combine phone code and number for new specialist
            if (payload.new_specialist && payload.new_specialist.phone) {
                payload.new_specialist.phone = phoneCode + payload.new_specialist.phone;
            }
        }

        try {
            const response = await ApiService.request({
                method: isEdit ? 'PUT' : 'POST',
                url: isEdit ? `admin/specialist-visits/${id}` : 'admin/specialist-visits',
                data: payload,
            });

            if (response.data.status) {
                toast.success(response.data.message);
                navigate('/specialist-visits');
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Something went wrong');
            if (error.response?.data?.errors) {
                // handle validation errors if needed
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            {isEdit ? 'Edit Specialist Visit' : 'Create Specialist Visit'}
                        </h1>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card">
                        <div className="card-body p-9">
                            {loading && <Loader />}
                            <form onSubmit={handleSubmit} className="form">
                                {!isEdit && (
                                    <div className="row mb-8">
                                        <div className="col-lg-12">
                                            <div className="d-flex align-items-center">
                                                <label className="form-check form-check-custom form-check-solid me-10">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="specialist_type"
                                                        value="existing"
                                                        checked={specialistType === 'existing'}
                                                        onChange={() => setSpecialistType('existing')}
                                                    />
                                                    <span className="form-check-label fw-bold">Select Existing Specialist</span>
                                                </label>
                                                <label className="form-check form-check-custom form-check-solid">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="specialist_type"
                                                        value="new"
                                                        checked={specialistType === 'new'}
                                                        onChange={() => setSpecialistType('new')}
                                                    />
                                                    <span className="form-check-label fw-bold">Create New Specialist</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {specialistType === 'existing' && (
                                    <div className="row mb-8">
                                        <div className="col-lg-6">
                                            <label className="required fs-6 fw-semibold mb-2">Select Specialist</label>
                                            <SpecialistSelect 
                                                value={formData.specialist_id}
                                                onChange={(val) => setFormData({...formData, specialist_id: val})}
                                                disabled={isEdit} // Disable changing specialist on edit for simplicity
                                            />
                                        </div>
                                    </div>
                                )}

                                {specialistType === 'new' && (
                                    <>
                                        <div className="row mb-8">
                                            <div className="col-lg-4">
                                                <label className="required fs-6 fw-semibold mb-2">Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-solid"
                                                    value={formData.new_specialist.name}
                                                    onChange={(e) => setFormData({...formData, new_specialist: {...formData.new_specialist, name: e.target.value}})}
                                                    required={specialistType === 'new'}
                                                />
                                            </div>
                                            <div className="col-lg-4">
                                                <label className=" fs-6 fw-semibold mb-2">Email</label>
                                                <input
                                                    type="email"
                                                    className="form-control form-control-solid"
                                                    value={formData.new_specialist.email}
                                                    onChange={(e) => setFormData({...formData, new_specialist: {...formData.new_specialist, email: e.target.value}})}
                                                    // required={specialistType === 'new'}
                                                />
                                            </div>
                                            <div className="col-lg-4">
                                                <label className="required fs-6 fw-semibold mb-2">Phone</label>
                                                <div className="input-group">
                                                    <input 
                                                        type="text" 
                                                        className="form-control form-control-solid w-80px" 
                                                        value={phoneCode}
                                                        onChange={(e) => setPhoneCode(e.target.value)}
                                                        placeholder="+255"
                                                        style={{ maxWidth: '80px' }}
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-solid"
                                                        value={formData.new_specialist.phone}
                                                        onChange={(e) => {
                                                            const val = e.target.value.replace(/\D/g, '');
                                                            setFormData({...formData, new_specialist: {...formData.new_specialist, phone: val}})
                                                        }}
                                                        placeholder="Phone Number"
                                                        required={specialistType === 'new'}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row mb-8">
                                            <div className="col-lg-6">
                                                <label className="required fs-6 fw-semibold mb-2">Username</label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-solid"
                                                    value={formData.new_specialist.username}
                                                    onChange={(e) => setFormData({...formData, new_specialist: {...formData.new_specialist, username: e.target.value}})}
                                                    required={specialistType === 'new'}
                                                />
                                            </div>
                                            <div className="col-lg-6">
                                                <label className="required fs-6 fw-semibold mb-2">Password</label>
                                                <input
                                                    type="password"
                                                    className="form-control form-control-solid"
                                                    value={formData.new_specialist.password}
                                                    onChange={(e) => setFormData({...formData, new_specialist: {...formData.new_specialist, password: e.target.value}})}
                                                    required={specialistType === 'new'}
                                                    minLength={6}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="row mb-8">
                                    <div className="col-lg-4">
                                        <label className="required fs-6 fw-semibold mb-2">Visit Date</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-solid"
                                            value={formData.visit_date}
                                            onChange={(e) => setFormData({...formData, visit_date: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="col-lg-4">
                                        <label className="required fs-6 fw-semibold mb-2">Visit Time (From)</label>
                                        <input
                                            type="time"
                                            className="form-control form-control-solid"
                                            value={formData.visit_time_from}
                                            onChange={(e) => setFormData({...formData, visit_time_from: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="col-lg-4">
                                        <label className="required fs-6 fw-semibold mb-2">Visit Time (To)</label>
                                        <input
                                            type="time"
                                            className="form-control form-control-solid"
                                            value={formData.visit_time_to}
                                            onChange={(e) => setFormData({...formData, visit_time_to: e.target.value})}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="row mb-8">
                                    <div className="col-lg-4">
                                        <label className="required fs-6 fw-semibold mb-2">Clinic Fee</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-control form-control-solid"
                                            value={formData.clinic_fee}
                                            onChange={(e) => setFormData({...formData, clinic_fee: e.target.value})}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="d-flex justify-content-end">
                                    <button type="button" onClick={() => navigate('/specialist-visits')} className="btn btn-light me-3">
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={loading}>
                                        {loading ? 'Saving...' : 'Save Visit'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Simple internal component to fetch and select specialists
const SpecialistSelect = ({ value, onChange, disabled }) => {
    const [specialists, setSpecialists] = useState([]);
    
    useEffect(() => {
        const fetchSpecs = async () => {
            try {
                 const response = await ApiService.request({
                    method: 'GET',
                    url: 'admin/users?role=specialist', 
                });
                if (response.data.status) {
                    const specs = response.data.data.users.filter(u => u.role === 'specialist');
                    setSpecialists(specs);
                }
            } catch (e) {
                console.warn("Could not fetch specialists", e);
            }
        };
        fetchSpecs();
    }, []);

    return (
        <select 
            className="form-select form-select-solid" 
            value={value} 
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            required
        >
            <option value="">Select a Specialist</option>
            {specialists.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.phone})</option>
            ))}
        </select>
    );
};

export default AddUpdate;
